/**
 * Client-side Monte Carlo simulation using block bootstrap.
 *
 * Takes an equity curve, reshuffles daily returns in 20-day blocks,
 * and produces distribution statistics + sampled equity paths.
 */

export interface MonteCarloConfig {
  numSimulations: number; // e.g. 10,000
  blockSize: number; // e.g. 20 (trading days per block)
  confidenceLevels: number[]; // e.g. [0.05, 0.25, 0.75, 0.95]
  samplePaths: number; // how many paths to return for rendering (e.g. 50)
}

export interface MonteCarloStats {
  medianFinalValue: number;
  meanFinalValue: number;
  medianCAGR: number;
  percentile5CAGR: number;
  percentile95CAGR: number;
  medianMaxDrawdown: number;
  percentile5MaxDrawdown: number; // worst-case DD (5th percentile)
  percentile95MaxDrawdown: number;
  ruinProbability: number; // probability of losing > 50%
  profitProbability: number; // probability of ending above start
  originalCAGR: number;
  originalMaxDrawdown: number;
  years: number;
}

export interface MonteCarloResult {
  stats: MonteCarloStats;
  /** Confidence bands: { date, p5, p25, median, p75, p95 }[] */
  bands: MonteCarloBand[];
  /** A few sample equity paths for rendering */
  paths: MonteCarloPath[];
  computeTimeMs: number;
}

export interface MonteCarloBand {
  date: string;
  p5: number;
  p25: number;
  median: number;
  p75: number;
  p95: number;
  original: number;
  benchmark?: number;
}

export interface MonteCarloPath {
  points: number[]; // portfolio values, same length as input equity
}

const DEFAULT_CONFIG: MonteCarloConfig = {
  numSimulations: 5000,
  blockSize: 20,
  confidenceLevels: [0.05, 0.25, 0.75, 0.95],
  samplePaths: 30,
};

/**
 * Compute daily returns from equity curve values.
 */
function computeDailyReturns(values: number[]): number[] {
  const returns: number[] = [];
  for (let i = 1; i < values.length; i++) {
    returns.push(values[i] / values[i - 1] - 1);
  }
  return returns;
}

/**
 * Compute max drawdown from an equity series.
 */
function maxDrawdown(values: number[]): number {
  let peak = values[0];
  let worstDD = 0;
  for (let i = 1; i < values.length; i++) {
    if (values[i] > peak) peak = values[i];
    const dd = (values[i] - peak) / peak;
    if (dd < worstDD) worstDD = dd;
  }
  return worstDD * 100; // as percentage (negative)
}

/**
 * CAGR from start/end values over N years.
 */
function cagr(startValue: number, endValue: number, years: number): number {
  if (years <= 0 || startValue <= 0) return 0;
  return (Math.pow(endValue / startValue, 1 / years) - 1) * 100;
}

/**
 * Block bootstrap: build a synthetic return series by sampling blocks.
 */
function blockBootstrap(
  dailyReturns: number[],
  blockSize: number,
  targetLength: number,
): number[] {
  const result: number[] = [];
  const n = dailyReturns.length;
  while (result.length < targetLength) {
    // Pick a random start index
    const start = Math.floor(Math.random() * (n - blockSize + 1));
    const end = Math.min(start + blockSize, n);
    for (let i = start; i < end && result.length < targetLength; i++) {
      result.push(dailyReturns[i]);
    }
  }
  return result;
}

/**
 * Compute percentile from a sorted array.
 */
function percentile(sorted: number[], p: number): number {
  const idx = p * (sorted.length - 1);
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  if (lo === hi) return sorted[lo];
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (idx - lo);
}

/**
 * Run Monte Carlo simulation on equity curve data.
 */
export function runMonteCarlo(
  equityDates: string[],
  equityValues: number[],
  config: Partial<MonteCarloConfig> = {},
): MonteCarloResult {
  const t0 = performance.now();
  const cfg = { ...DEFAULT_CONFIG, ...config };

  const n = equityValues.length;
  if (n < 2) {
    throw new Error("Need at least 2 equity points for Monte Carlo");
  }

  const dailyReturns = computeDailyReturns(equityValues);
  const startValue = equityValues[0];
  const numDays = dailyReturns.length;
  const years = numDays / 252;

  // Original metrics
  const originalFinal = equityValues[n - 1];
  const originalCAGR = cagr(startValue, originalFinal, years);
  const originalDD = maxDrawdown(equityValues);

  // Run simulations — store final values and max drawdowns for stats
  const finalValues: number[] = new Array(cfg.numSimulations);
  const maxDDs: number[] = new Array(cfg.numSimulations);

  // For bands: accumulate values at each time step
  // Use Float64Array for performance
  const timeSteps = numDays + 1; // includes starting point
  const allPaths: Float64Array[] = [];

  // Determine which sims to save as sample paths
  const sampleIndices = new Set<number>();
  const step = Math.max(1, Math.floor(cfg.numSimulations / cfg.samplePaths));
  for (let i = 0; i < cfg.samplePaths && i * step < cfg.numSimulations; i++) {
    sampleIndices.add(i * step);
  }

  // Pre-allocate arrays for band computation
  const columnValues: number[][] = Array.from({ length: timeSteps }, () => []);

  for (let sim = 0; sim < cfg.numSimulations; sim++) {
    const syntheticReturns = blockBootstrap(dailyReturns, cfg.blockSize, numDays);

    // Build equity curve from returns
    let value = startValue;
    let peak = value;
    let worstDD = 0;

    columnValues[0].push(value);

    const pathPoints = sampleIndices.has(sim) ? [value] : null;

    for (let d = 0; d < numDays; d++) {
      value *= 1 + syntheticReturns[d];
      if (value > peak) peak = value;
      const dd = (value - peak) / peak;
      if (dd < worstDD) worstDD = dd;

      columnValues[d + 1].push(value);
      if (pathPoints) pathPoints.push(value);
    }

    finalValues[sim] = value;
    maxDDs[sim] = worstDD * 100;

    if (pathPoints) {
      allPaths.push(new Float64Array(pathPoints));
    }
  }

  // Sort final values and drawdowns for percentile computation
  const sortedFinals = [...finalValues].sort((a, b) => a - b);
  const sortedDDs = [...maxDDs].sort((a, b) => a - b); // most negative first

  // Compute CAGR distribution
  const cagrs = finalValues.map((fv) => cagr(startValue, fv, years));
  const sortedCAGRs = [...cagrs].sort((a, b) => a - b);

  // Stats
  const stats: MonteCarloStats = {
    medianFinalValue: percentile(sortedFinals, 0.5),
    meanFinalValue: finalValues.reduce((a, b) => a + b, 0) / cfg.numSimulations,
    medianCAGR: percentile(sortedCAGRs, 0.5),
    percentile5CAGR: percentile(sortedCAGRs, 0.05),
    percentile95CAGR: percentile(sortedCAGRs, 0.95),
    medianMaxDrawdown: percentile(sortedDDs, 0.5),
    percentile5MaxDrawdown: percentile(sortedDDs, 0.05),
    percentile95MaxDrawdown: percentile(sortedDDs, 0.95),
    ruinProbability: finalValues.filter((v) => v < startValue * 0.5).length / cfg.numSimulations,
    profitProbability: finalValues.filter((v) => v > startValue).length / cfg.numSimulations,
    originalCAGR,
    originalMaxDrawdown: originalDD,
    years,
  };

  // Build bands — sample every Nth point to keep data manageable
  const maxBandPoints = 500;
  const bandStep = Math.max(1, Math.floor(timeSteps / maxBandPoints));
  const bands: MonteCarloBand[] = [];

  for (let t = 0; t < timeSteps; t += bandStep) {
    const col = columnValues[t].sort((a, b) => a - b);
    bands.push({
      date: equityDates[Math.min(t, equityDates.length - 1)],
      p5: percentile(col, 0.05),
      p25: percentile(col, 0.25),
      median: percentile(col, 0.5),
      p75: percentile(col, 0.75),
      p95: percentile(col, 0.95),
      original: equityValues[Math.min(t, n - 1)],
    });
  }

  // Ensure last point is included
  if ((timeSteps - 1) % bandStep !== 0) {
    const col = columnValues[timeSteps - 1].sort((a, b) => a - b);
    bands.push({
      date: equityDates[equityDates.length - 1],
      p5: percentile(col, 0.05),
      p25: percentile(col, 0.25),
      median: percentile(col, 0.5),
      p75: percentile(col, 0.75),
      p95: percentile(col, 0.95),
      original: equityValues[n - 1],
    });
  }

  // Convert sample paths
  const paths: MonteCarloPath[] = allPaths.map((p) => ({
    points: Array.from(p),
  }));

  return {
    stats,
    bands,
    paths,
    computeTimeMs: performance.now() - t0,
  };
}
